/**
 * `sandbox` for every iframe that renders model-generated HTML — the chat preview and
 * both published viewers. All three must use this exact value so the preview stays a
 * truthful preview of what publishing produces.
 *
 * Deliberately omits **`allow-same-origin`**: a `srcdoc` iframe granted it inherits the
 * app's origin, and a same-origin iframe shares the tab's `sessionStorage` — where the
 * Keycloak tokens live (see `useAuthTokens`). Together with `allow-scripts` that would
 * hand generated markup the signed-in user's session, so the two must never co-occur.
 *
 * **`allow-popups-to-escape-sandbox`** keeps `target="_blank"` links usable: without it
 * the opened tab inherits these flags (including the absent `allow-same-origin`), loads
 * at origin `null`, and every subresource request there fails CORS.
 *
 * The resulting opaque origin makes `localStorage` / `sessionStorage` throw on access;
 * `sandboxedStorageShimScriptSource` substitutes in-memory stores so generated pages
 * degrade to "not persisted" instead of dying on the first line of script.
 */
export const GENERATED_HTML_SANDBOX =
  'allow-scripts allow-forms allow-popups allow-downloads allow-popups-to-escape-sandbox' as const

/**
 * `sandbox` for a custom inline visual (```inline-html, from `render_custom_inline_html`).
 *
 * No **`allow-scripts`**, and that is the whole defence. The API refuses scripts, frames, forms
 * and event handlers only in fragments the tool writes; any ```inline-html fence renders here —
 * one the model typed into its reply, or one a user pasted — and none of those were checked.
 * With scripts off, nothing in any of them can run; the document's own CSP
 * (`buildInlineHtmlDocument`) also stops it loading anything.
 *
 * **`allow-same-origin`** is what lets the chat measure the visual and size the frame to fit it
 * instead of scrolling. It puts the frame in the app's origin, which is safe only because
 * scripts are off: the danger described on `GENERATED_HTML_SANDBOX` is the two flags together.
 * Never add `allow-scripts` to this value.
 *
 * Popups stay allowed, and escape the sandbox, so a `target="_blank"` link still opens normally.
 */
export const INLINE_VISUAL_SANDBOX =
  'allow-same-origin allow-popups allow-popups-to-escape-sandbox' as const
